import React from 'react';

/* ============================================================
   Bespoke cinematic "stills" — one scene per production.
   Transparent backgrounds: every scene sits directly on its
   section's paper or ink ground, no frames, no letterbox.
   Palette: paper / ink / vermillion / amber.
   ============================================================ */

const MONO = "'JetBrains Mono Variable','JetBrains Mono',ui-monospace,monospace";
const DISP = "'Bebas Neue','Space Grotesk',sans-serif";

const INK = 'var(--ink)', INK2 = 'var(--ink-2)', INK3 = 'var(--ink-3)';
const PAPER = 'var(--paper)', PAPER3 = 'var(--paper-3)';
const OR = 'var(--orange)', OR2 = 'var(--orange-2)', OR3 = 'var(--orange-3)';
const AMB = 'var(--amber)', WARM = 'var(--warm)';

const Root = ({ children, label, fit = 'slice' }) => (
  <svg viewBox="0 0 480 330" preserveAspectRatio={`xMidYMid ${fit}`} className="h-full w-full" role="img" aria-label={label}>{children}</svg>
);
const T = ({ x, y, s = 9, c = WARM, o = 1, ls = 1.5, a = 'start', f = MONO, children }) => (
  <text x={x} y={y} fontFamily={f} fontSize={s} fill={c} opacity={o} letterSpacing={ls} textAnchor={a}>{children}</text>
);
const Lines = ({ x, y, w, n = 3, c = WARM, o = 0.35, gap = 9, h = 3.5 }) => (
  <g>{Array.from({ length: n }, (_, i) => (<rect key={i} x={x} y={y + i * gap} width={w * (i === n - 1 ? 0.62 : 1)} height={h} rx={h / 2} fill={c} opacity={o} />))}</g>
);
const Chrome = ({ x, y, w, h, fill, stroke, dot }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx="6" fill={fill} stroke={stroke} strokeWidth="1" />
    <line x1={x} y1={y + 16} x2={x + w} y2={y + 16} stroke={stroke} strokeWidth="1" />
    {[0, 1, 2].map((i) => (<circle key={i} cx={x + 9 + i * 8} cy={y + 8} r="2" fill={i === 0 ? OR2 : dot} opacity={i === 0 ? 1 : 0.5} />))}
  </g>
);
const Candles = ({ x, y, w, h, seed = 0, up = OR2, dn = WARM }) => {
  const n = 8, cw = w / n;
  const hs = [0.55, 0.8, 0.4, 0.9, 0.62, 0.35, 0.75, 0.5];
  return (<g>{hs.map((v, i) => {
    const k = (i + seed) % hs.length; const bh = hs[k] * h * 0.55; const cy = y + h * 0.22 + ((i * 37 + seed * 53) % (h * 0.28));
    const isUp = (i + seed) % 3 !== 1;
    return (<g key={i}>
      <line x1={x + i * cw + cw / 2} y1={cy - bh * 0.35} x2={x + i * cw + cw / 2} y2={cy + bh + bh * 0.3} stroke={isUp ? up : dn} strokeWidth="1" opacity={isUp ? 0.9 : 0.45} />
      <rect x={x + i * cw + cw * 0.22} y={cy} width={cw * 0.56} height={bh} rx="1" fill={isUp ? up : dn} opacity={isUp ? 0.95 : 0.4} />
    </g>);
  })}</g>);
};
const Spark = ({ x, y, w, h, c = OR2, o = 1, fillO = 0.14 }) => (
  <g>
    <path d={`M${x} ${y + h * 0.8} L${x + w * 0.18} ${y + h * 0.45} L${x + w * 0.36} ${y + h * 0.6} L${x + w * 0.55} ${y + h * 0.25} L${x + w * 0.74} ${y + h * 0.42} L${x + w} ${y + h * 0.08}`} fill="none" stroke={c} strokeWidth="1.6" opacity={o} />
    <path d={`M${x} ${y + h * 0.8} L${x + w * 0.18} ${y + h * 0.45} L${x + w * 0.36} ${y + h * 0.6} L${x + w * 0.55} ${y + h * 0.25} L${x + w * 0.74} ${y + h * 0.42} L${x + w} ${y + h * 0.08} L${x + w} ${y + h} L${x} ${y + h} Z`} fill={c} opacity={fillO} />
  </g>
);
const BarRow = ({ x, y, w, h, n = 6, c = OR2, o = 0.9 }) => {
  const vs = [0.45, 0.75, 0.55, 0.95, 0.65, 0.82, 0.5, 0.7];
  const bw = w / n;
  return (<g>{Array.from({ length: n }, (_, i) => (<rect key={i} x={x + i * bw + bw * 0.18} y={y + h * (1 - vs[i % vs.length])} width={bw * 0.64} height={h * vs[i % vs.length]} rx="1.5" fill={c} opacity={o - i * 0.06} />))}</g>);
};
const Donut = ({ cx, cy, r, c1 = OR2, c2 = AMB, track = 'rgba(255,255,255,0.12)' }) => (
  <g fill="none" strokeWidth={r * 0.42}>
    <circle cx={cx} cy={cy} r={r} stroke={track} />
    <circle cx={cx} cy={cy} r={r} stroke={c1} strokeDasharray={`${r * 3.7} ${r * 6.28}`} transform={`rotate(-90 ${cx} ${cy})`} strokeLinecap="round" />
    <circle cx={cx} cy={cy} r={r} stroke={c2} strokeDasharray={`${r * 1.4} ${r * 6.28}`} transform={`rotate(135 ${cx} ${cy})`} strokeLinecap="round" />
  </g>
);
const Plane = ({ x, y, s = 1, c = OR2 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}><path d="M0 8 L26 0 L18 20 L12 13 Z M26 0 L12 13" fill="none" stroke={c} strokeWidth="1.6" strokeLinejoin="round" /></g>
);
const Chip = ({ x, y, w, label, dark, c }) => (
  <g>
    <rect x={x} y={y} width={w} height="14" rx="7" fill="none" stroke={c || (dark ? 'rgba(255,255,255,0.3)' : 'rgba(18,17,16,0.3)')} strokeWidth="1" />
    <T x={x + w / 2} y={y + 10} s={7.5} a="middle" c={c || (dark ? WARM : INK)} o={0.85}>{label}</T>
  </g>
);

/* ---------- HERO · mission control (sits on the orange ground) ---------- */
export function HeroScene() {
  return (
    <Root label="Mission control — enterprise dashboards" fit="meet">
      {/* perspective floor */}
      <g stroke={INK} opacity="0.14">{[0, 1, 2, 3, 4].map((i) => (<line key={i} x1={240 - (i + 1) * 90} y1="330" x2={240 - i * 26} y2="240" />))}{[0, 1, 2, 3, 4].map((i) => (<line key={'r' + i} x1={240 + (i + 1) * 90} y1="330" x2={240 + i * 26} y2="240" />))}{[250, 268, 292].map((y, i) => (<line key={'h' + i} x1="30" y1={y} x2="450" y2={y} />))}</g>
      {/* side monitors */}
      <g opacity="0.92">
        <rect x="34" y="98" width="112" height="84" rx="4" fill={INK2} transform="skewY(4)" />
        <g transform="skewY(4)"><Spark x={44} y={116} w={92} h={40} c={AMB} o={0.8} /><Lines x={44} y={164} w={70} n={1} c={WARM} o={0.3} /></g>
        <rect x="334" y="88" width="112" height="84" rx="4" fill={INK2} transform="skewY(-4)" />
        <g transform="skewY(-4)"><Donut cx={366} cy={128} r={16} /><BarRow x={392} y={106} w={44} h={42} n={4} /><Lines x={344} y={158} w={70} n={1} c={WARM} o={0.3} /></g>
      </g>
      {/* main screen */}
      <rect x="152" y="52" width="176" height="140" rx="5" fill={INK2} stroke={INK} strokeWidth="1.5" />
      <rect x="152" y="52" width="176" height="18" rx="5" fill="rgba(255,255,255,0.05)" />
      <circle cx="162" cy="61" r="2.4" fill={OR2} className="blink" />
      <T x={172} y={64} s={7.5} o={0.75}>HORIZON · LIVE</T>
      <T x={318} y={64} s={7} o={0.45} a="end">BI-04</T>
      {/* KPI row */}
      {[0, 1, 2].map((i) => (<g key={i}><rect x={160 + i * 55} y={78} width={50} height={26} rx="3" fill="rgba(255,255,255,0.06)" /><T x={165 + i * 55} y={90} s={9.5} c={i === 1 ? AMB : OR2} f={DISP} ls={0.5}>{['5,847', '$2.1M', '95%'][i]}</T><rect x={165 + i * 55} y={95} width={30} height={3} rx={1.5} fill={WARM} opacity={0.25} /></g>))}
      <Spark x={160} y={112} w={160} h={44} />
      <BarRow x={160} y={162} w={104} h={22} n={8} o={0.7} />
      <g><circle cx="300" cy="172" r="12" fill="none" stroke={AMB} strokeWidth="1" strokeDasharray="2 3" opacity="0.7" /><circle cx="300" cy="172" r="4" fill={AMB} opacity="0.9" /></g>
      {/* stand + desk */}
      <rect x="230" y="192" width="20" height="16" fill={INK3} /><rect x="196" y="208" width="88" height="5" rx="2.5" fill={INK3} />
      <rect x="60" y="216" width="360" height="8" rx="3" fill="rgba(18,17,16,0.2)" />
      {/* radar */}
      <g opacity="0.8"><circle cx="416" cy="46" r="24" fill="none" stroke={INK} strokeWidth="1" opacity="0.4" /><circle cx="416" cy="46" r="14" fill="none" stroke={INK} strokeWidth="1" opacity="0.28" /><g className="sweep"><line x1="416" y1="46" x2="416" y2="24" stroke={INK} strokeWidth="1.6" /><circle cx="416" cy="24" r="2" fill={INK} /></g></g>
      {/* stars */}
      {[[70, 40, 0], [110, 66, 0.8], [372, 26, 1.3], [56, 148, 0.4]].map(([x, y, d], i) => (<circle key={i} cx={x} cy={y} r="1.8" fill={INK} opacity="0.7" className="twk" style={{ animationDelay: `${d}s` }} />))}
      <T x={62} y={240} s={7} c={INK} o={0.55}>INT. WAR-ROOM — NIGHT</T>
      <T x={418} y={240} s={7} c={INK} o={0.55} a="end">TAKE 22</T>
    </Root>
  );
}

/* ---------- ACT I ---------- */
function SceneOgmob() {
  return (
    <Root label="Pocket HR mobile companion">
      {/* floating side cards */}
      <g opacity="0.7"><rect x="52" y="92" width="108" height="64" rx="5" fill={PAPER} stroke="rgba(18,17,16,0.15)" /><Spark x={62} y={104} w={88} h={36} c={OR3} fillO={0.1} /><Lines x={62} y={146} w={60} n={1} c={INK} o={0.3} /></g>
      <g opacity="0.7"><rect x="322" y="130" width="106" height="70" rx="5" fill={PAPER} stroke="rgba(18,17,16,0.15)" /><BarRow x={332} y={140} w={86} h={38} n={5} c={OR3} /><Lines x={332} y={186} w={56} n={1} c={INK} o={0.3} /></g>
      {/* phone */}
      <rect x="188" y="26" width="104" height="278" rx="18" fill={INK} />
      <rect x="194" y="32" width="92" height="266" rx="13" fill={PAPER} />
      <rect x="222" y="36" width="36" height="5" rx="2.5" fill={INK} opacity="0.8" />
      <T x={202} y={58} s={10} c={INK} f={DISP} ls={1}>POCKET HR</T>
      <circle cx="276" cy="54" r="4" fill="none" stroke={OR3} strokeWidth="1.2" /><circle cx="276" cy="54" r="1.4" fill={OR3} />
      {/* KPI cards */}
      <rect x="202" y="66" width="36" height="30" rx="4" fill={INK} /><T x={206} y={80} s={8.5} c={OR2} f={DISP}>5.8K</T><rect x={206} y={86} width={20} height={2.5} rx={1.25} fill={WARM} opacity={0.4} />
      <rect x="242" y="66" width="36" height="30" rx="4" fill={OR} /><T x={246} y={80} s={8.5} c={WARM} f={DISP}>98%</T><rect x={246} y={86} width={20} height={2.5} rx={1.25} fill={WARM} opacity={0.6} />
      {/* list rows */}
      {[0, 1, 2, 3].map((i) => (<g key={i}><rect x="202" y={104 + i * 30} width="76" height="24" rx="4" fill="rgba(18,17,16,0.06)" /><circle cx="212" cy={116 + i * 30} r="5" fill={i === 1 ? OR : INK} opacity={i === 1 ? 1 : 0.75} /><Lines x={221} y={110 + i * 30} w={50} n={2} c={INK} o={0.4} gap={7} h={3} /></g>))}
      <Spark x={202} y={228} w={76} h={30} c={OR3} />
      {/* tab bar */}
      <rect x="202" y="268" width="76" height="20" rx="6" fill={INK} />
      {[0, 1, 2, 3, 4, 5].map((i) => (<circle key={i} cx={211 + i * 12} cy="278" r="2.4" fill={i === 0 ? OR2 : WARM} opacity={i === 0 ? 1 : 0.4} />))}
      <Chip x={330} y={92} w={62} label="OFFLINE ✓" c={OR3} />
      <Chip x={70} y={182} w={44} label="PWA" c={OR3} />
    </Root>
  );
}
function SceneOfogh2() {
  return (
    <Root label="Horizon Reports reporting suite">
      <Chrome x={46} y={34} w={388} h={252} fill={PAPER} stroke="rgba(18,17,16,0.2)" dot={INK} />
      {/* sidebar */}
      <rect x="46" y="50" width="64" height="236" fill="rgba(18,17,16,0.05)" />
      {[0, 1, 2, 3, 4].map((i) => (<rect key={i} x="56" y={62 + i * 22} width={i === 1 ? 44 : 36} height="6" rx="3" fill={i === 1 ? OR3 : INK} opacity={i === 1 ? 0.9 : 0.3} />))}
      {/* header */}
      <T x={122} y={66} s={10} c={INK} f={DISP} ls={1}>REPORTS · Q2</T>
      <rect x="352" y="56" width="70" height="14" rx="7" fill={OR} /><T x={387} y={66} s={7.5} c={WARM} a="middle">+ EXPORT</T>
      {/* 2x2 report cards */}
      <rect x="122" y="80" width="146" height="92" rx="4" fill="#fff" opacity="0.55" stroke="rgba(18,17,16,0.12)" />
      <BarRow x={132} y={92} w={126} h={62} n={7} c={OR3} /><Lines x={132} y={162} w={80} n={1} c={INK} o={0.35} />
      <rect x="276" y="80" width="146" height="92" rx="4" fill="#fff" opacity="0.55" stroke="rgba(18,17,16,0.12)" />
      <Spark x={286} y={96} w={126} h={54} c={OR3} /><Lines x={286} y={162} w={80} n={1} c={INK} o={0.35} />
      <rect x="122" y="180" width="146" height="92" rx="4" fill="#fff" opacity="0.55" stroke="rgba(18,17,16,0.12)" />
      <Donut cx={162} cy={222} r={22} c1={OR3} c2={AMB} track="rgba(18,17,16,0.1)" /><Lines x={196} y={204} w={60} n={4} c={INK} o={0.35} gap={11} />
      <rect x="276" y="180" width="146" height="92" rx="4" fill="#fff" opacity="0.55" stroke="rgba(18,17,16,0.12)" />
      {[0, 1, 2, 3, 4].map((i) => (<g key={i}><rect x="286" y={192 + i * 15} width="126" height="1.5" fill={INK} opacity="0.12" /><Lines x={286} y={196 + i * 15} w={i % 2 ? 96 : 116} n={1} c={INK} o={0.3} h={3} /></g>))}
      <Chip x={286} y={252} w={40} label="XLSX" c={OR3} /><Chip x={332} y={252} w={34} label="PDF" c={OR3} />
    </Root>
  );
}
function SceneMns() {
  return (
    <Root label="Paycore type-safe payroll pipeline">
      {/* DB cylinder */}
      <g><ellipse cx="88" cy="86" rx="34" ry="11" fill={PAPER3} stroke={INK} strokeWidth="1.2" /><path d="M54 86 V138 A34 11 0 0 0 122 138 V86" fill={PAPER3} stroke={INK} strokeWidth="1.2" /><ellipse cx="88" cy="112" rx="34" ry="11" fill="none" stroke={INK} strokeWidth="0.8" opacity="0.4" /><T x={88} y={166} s={8} c={INK} a="middle" o={0.7}>MYSQL · TIDB</T></g>
      {/* API box */}
      <g><rect x="192" y="72" width="96" height="72" rx="6" fill={INK} /><T x={240} y={102} s={15} c={OR2} f={DISP} a="middle" ls={2}>tRPC 11</T><T x={240} y={122} s={8} c={WARM} a="middle" o={0.55}>DRIZZLE ORM</T><T x={240} y={166} s={8} c={INK} a="middle" o={0.7}>CONTRACTS</T></g>
      {/* UI window */}
      <g><Chrome x={344} y={64} w={104} h={88} fill={PAPER} stroke={INK} dot={INK} /><Lines x={354} y={90} w={84} n={3} c={INK} o={0.4} gap={11} /><rect x="354" y="126" width="52" height="14" rx="3" fill={OR} /><T x={380} y={136} s={7.5} c={WARM} a="middle">PAYROLL</T><T x={396} y={170} s={8} c={INK} a="middle" o={0.7}>REACT 19</T></g>
      {/* typed arrows */}
      <g stroke={OR3} strokeWidth="1.6" fill="none"><path d="M126 108 H188 M182 103 L188 108 L182 113" /><path d="M292 108 H340 M334 103 L340 108 L334 113" /></g>
      <Chip x={136} y={86} w={36} label="<T>" c={OR3} /><Chip x={298} y={86} w={36} label="<T>" c={OR3} />
      {/* terminal */}
      <rect x="88" y="200" width="304" height="86" rx="6" fill={INK} />
      <line x1="88" y1="216" x2="392" y2="216" stroke="rgba(255,255,255,0.15)" />
      {[0, 1, 2].map((i) => (<circle key={i} cx={98 + i * 9} cy="208" r="2" fill={i === 0 ? OR2 : WARM} opacity={i === 0 ? 1 : 0.4} />))}
      <T x={100} y={234} s={8} c={WARM} o={0.8}>$ vitest run --coverage</T>
      <T x={100} y={250} s={8} c={OR2}>✓ payroll.import  ✓ contracts  ✓ e2e-types</T>
      <T x={100} y={266} s={8} c={WARM} o={0.55}>42 passed · 0 failed · xlsx pipeline OK</T>
      <rect x="100" y="272" width="6" height="9" fill={OR2} className="blink" />
    </Root>
  );
}
function SceneSplit() {
  return (
    <Root label="Dualstack modular portal">
      {/* top mono box */}
      <rect x="168" y="30" width="144" height="54" rx="6" fill={INK} />
      <T x={240} y={53} s={12} c={WARM} f={DISP} a="middle" ls={2}>DUALSTACK</T>
      <T x={240} y={70} s={7.5} c={WARM} a="middle" o={0.5}>ONE CODEBASE · 2025</T>
      {/* split arrows */}
      <g stroke={OR3} strokeWidth="1.6" fill="none"><path d="M216 88 C 190 116, 160 118, 148 138 M152 130 L148 138 L157 139" /><path d="M264 88 C 290 116, 320 118, 332 138 M323 139 L332 138 L328 130" /></g>
      {/* client pkg */}
      <Chrome x={62} y={146} w={162} h={104} fill={PAPER} stroke={INK} dot={INK} />
      <T x={74} y={178} s={10} c={INK} f={DISP} ls={1}>CLIENT PACKAGE</T>
      <Lines x={74} y={188} w={120} n={2} c={INK} o={0.35} gap={10} />
      <rect x="74" y="214" width="60" height="14" rx="3" fill="rgba(18,17,16,0.08)" /><T x={104} y={224} s={7.5} c={INK} a="middle" o={0.8}>REACT · TS</T>
      <Chip x={142} y={214} w={54} label="v2.4.0" c={OR3} />
      {/* server pkg */}
      <Chrome x={256} y={146} w={162} h={104} fill={INK} stroke="rgba(255,255,255,0.2)" dot={WARM} />
      <T x={268} y={178} s={10} c={WARM} f={DISP} ls={1}>SERVER PACKAGE</T>
      <Lines x={268} y={188} w={120} n={2} c={WARM} o={0.3} gap={10} />
      <rect x="268" y="214" width="66" height="14" rx="3" fill="rgba(255,255,255,0.1)" /><T x={301} y={224} s={7.5} c={WARM} a="middle" o={0.8}>NODE · API</T>
      <Chip x={342} y={214} w={54} label="v2.4.1" c={OR2} />
      {/* independent pipelines */}
      <g stroke={OR3} strokeWidth="1.4" fill="none" opacity="0.85">
        <path d="M100 262 H150 M144 258 L150 262 L144 266" /><circle cx="88" cy="262" r="7" /><path d="M85 262 l2.5 2.5 4.5-5" />
        <path d="M294 262 H344 M338 258 L344 262 L338 266" /><circle cx="282" cy="262" r="7" /><path d="M279 262 l2.5 2.5 4.5-5" />
      </g>
      <T x={166} y={266} s={7.5} c={INK} o={0.6}>DEPLOY A</T><T x={360} y={266} s={7.5} c={INK} o={0.6}>DEPLOY B</T>
      <T x={240} y={296} s={8} c={INK} a="middle" o={0.55}>INDEPENDENT BUILDS · SEPARATE RELEASE CADENCE</T>
    </Root>
  );
}
function SceneFrost() {
  return (
    <Root label="Frostline themed HR frontend">
      {/* split card: dark / light */}
      <rect x="70" y="44" width="340" height="220" rx="8" fill={PAPER} stroke="rgba(18,17,16,0.2)" />
      <path d="M70 44 H260 L200 264 H70 Z" fill={INK} opacity="0.96" />
      {/* dark side form */}
      <T x={88} y={74} s={11} c={WARM} f={DISP} ls={1.5}>FROSTLINE · DARK</T>
      {[0, 1, 2].map((i) => (<g key={i}><rect x="88" y={86 + i * 30} width="96" height="20" rx="4" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.2)" /><Lines x={94} y={94 + i * 30} w={56} n={1} c={WARM} o={0.4} h={3.5} /><circle cx={172} cy={96 + i * 30} r="5.5" fill="none" stroke={OR2} strokeWidth="1.3" /><path d={`M${169.5} ${96 + i * 30} l2 2 3.5-4`} stroke={OR2} strokeWidth="1.3" fill="none" /></g>))}
      <rect x="88" y="180" width="72" height="18" rx="4" fill={OR} /><T x={124} y={192} s={7.5} c={WARM} a="middle">SUBMIT</T>
      {/* light side form */}
      <T x={392} y={74} s={11} c={INK} f={DISP} ls={1.5} a="end">LIGHT</T>
      {[0, 1, 2].map((i) => (<g key={i}><rect x="288" y={86 + i * 30} width="96" height="20" rx="4" fill="#fff" opacity="0.7" stroke="rgba(18,17,16,0.25)" /><Lines x={294} y={94 + i * 30} w={56} n={1} c={INK} o={0.35} h={3.5} /><circle cx={372} cy={96 + i * 30} r="5.5" fill="none" stroke={OR3} strokeWidth="1.3" /><path d={`M${369.5} ${96 + i * 30} l2 2 3.5-4`} stroke={OR3} strokeWidth="1.3" fill="none" /></g>))}
      <rect x="288" y="180" width="72" height="18" rx="4" fill="none" stroke={OR3} strokeWidth="1.3" /><T x={324} y={192} s={7.5} c={OR3} a="middle">SUBMIT</T>
      {/* theme toggle */}
      <rect x="216" y="278" width="48" height="18" rx="9" fill={INK} /><circle cx="253" cy="287" r="6.5" fill={OR2} />
      {/* snowflakes */}
      {[[110, 236, 1], [330, 226, 0.8], [246, 60, 0.9]].map(([x, y, s], i) => (<g key={i} transform={`translate(${x} ${y}) scale(${s})`} stroke={i === 2 ? OR2 : AMB} strokeWidth="1.2" opacity="0.8"><path d="M0 -8 V8 M-7 -4 L7 4 M7 -4 L-7 4" /><path d="M0 -8 l-2.5 2.5 M0 -8 l2.5 2.5 M0 8 l-2.5-2.5 M0 8 l2.5-2.5" /></g>))}
      <Chip x={200} y={110} w={38} label="ZOD" c={OR2} />
      <Chip x={196} y={132} w={46} label="RADIX" c={OR2} />
    </Root>
  );
}

/* ---------- ACT II ---------- */
function SceneBots() {
  return (
    <Root label="Botstation — one Telegram session, three products">
      {/* telegram session window */}
      <Chrome x={52} y={22} w={376} h={280} fill={INK3} stroke="rgba(255,255,255,0.14)" dot={WARM} />
      {/* header */}
      <circle cx="74" cy="56" r="10" fill={OR} /><path d="M69 56 l3.5 3.5 6-7" stroke={WARM} strokeWidth="1.6" fill="none" />
      <T x={92} y={53} s={9} c={WARM}>BOTSTATION</T>
      <circle cx="96" cy="62" r="2" fill={OR2} className="blink" /><T x={103} y={65} s={7} c={WARM} o={0.5}>ONLINE · 3 BOTS · ONE SESSION</T>
      <line x1="52" y1="74" x2="428" y2="74" stroke="rgba(255,255,255,0.08)" />
      {/* user: /analyze */}
      <rect x="268" y="84" width="146" height="20" rx="8" fill={OR} opacity="0.95" />
      <T x={278} y={97} s={8} c={WARM}>/analyze BTCUSDT</T>
      {/* bot: analysis card */}
      <rect x="66" y="112" width="212" height="82" rx="8" fill="rgba(255,255,255,0.07)" />
      <T x={76} y={126} s={7.5} c={OR2}>MARKET ANALYST</T><T x={270} y={126} s={7} c={WARM} o={0.4} a="end">BTC/USDT</T>
      <Candles x={76} y={130} w={110} h={52} />
      <Spark x={196} y={136} w={70} h={18} c={AMB} fillO={0} />
      <T x={196} y={168} s={7} c={WARM} o={0.6}>RSI 61 · MACD +</T>
      <T x={196} y={182} s={8} c={OR2}>SIGNAL: LONG ↗</T>
      <T x={76} y={188} s={6.5} c={AMB} o={0.8}>VISION-LLM PASS ✓</T>
      {/* bot: VIP invite ticket */}
      <rect x="66" y="202" width="212" height="44" rx="8" fill="rgba(255,255,255,0.07)" />
      <rect x="76" y="210" width="128" height="28" rx="4" fill="none" stroke={WARM} strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
      <T x={84} y={222} s={7.5} c={WARM}>VIP INVITE · ONE-USE</T>
      <rect x="84" y="226" width="64" height="9" rx="4.5" fill={OR} /><T x={116} y={233} s={6.5} c={WARM} a="middle">7-DAY TRIAL</T>
      <circle cx="234" cy="224" r="12" fill="none" stroke={AMB} strokeWidth="1.4" /><T x={234} y={227.5} s={8} c={AMB} a="middle" f={DISP}>₮</T>
      <T x={252} y={221} s={6.5} c={WARM} o={0.6}>USDT</T><T x={252} y={230} s={6.5} c={WARM} o={0.6}>TON</T>
      {/* admin inline buttons */}
      <rect x="66" y="254" width="212" height="24" rx="8" fill="rgba(255,255,255,0.07)" />
      <T x={76} y={269} s={7} c={WARM} o={0.6}>RECEIPT REVIEW —</T>
      <rect x="176" y="259" width="44" height="14" rx="3" fill={OR} /><T x={198} y={269} s={8} c={WARM} a="middle">✓</T>
      <rect x="226" y="259" width="44" height="14" rx="3" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1" /><T x={248} y={269} s={8} c={WARM} a="middle" o={0.7}>✗</T>
      {/* user: status ping */}
      <rect x="300" y="140" width="114" height="20" rx="8" fill={OR} opacity="0.95" /><T x={310} y={153} s={8} c={WARM}>/vip status</T>
      <rect x="300" y="166" width="114" height="26" rx="8" fill="rgba(255,255,255,0.07)" /><T x={310} y={177} s={7} c={WARM} o={0.75}>DAY 5 / 7 · WARN</T><T x={310} y={187} s={7} c={OR2}>AUTO-KICK DAY 7</T>
      {/* scheduler + input */}
      <g transform="translate(300 206)">{[0, 1, 2].map((i) => (<g key={i}><circle cx={i * 26 + 8} cy="8" r="7" fill="none" stroke={i === 1 ? OR2 : WARM} strokeWidth="1.2" opacity={i === 1 ? 1 : 0.4} /><line x1={i * 26 + 8} y1="8" x2={i * 26 + 8} y2="3.5" stroke={i === 1 ? OR2 : WARM} strokeWidth="1.2" opacity={i === 1 ? 1 : 0.4} /><line x1={i * 26 + 8} y1="8" x2={i * 26 + 11.5} y2="10" stroke={i === 1 ? OR2 : WARM} strokeWidth="1.2" opacity={i === 1 ? 1 : 0.4} /></g>))}<T x={0} y={30} s={6.5} c={WARM} o={0.5}>DAILY JOBS · SYSTEMD</T></g>
      <rect x="66" y="284" width="316" height="12" rx="6" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.14)" />
      <Plane x={394} y={281} s={0.65} />
      {/* stack chips */}
      <Chip x={92} y={312} w={104} label="PYTHON · AIOGRAM" c={OR2} /><Chip x={204} y={312} w={48} label="REDIS" c={OR2} /><Chip x={260} y={312} w={52} label="SQLITE" c={OR2} /><Chip x={320} y={312} w={64} label="SYSTEMD" c={OR2} />
    </Root>
  );
}
function SceneReport() {
  return (
    <Root label="Insightdeck conversational analytics">
      {/* spreadsheet */}
      <g><rect x="42" y="70" width="120" height="150" rx="5" fill={PAPER} stroke="rgba(18,17,16,0.25)" /><rect x="42" y="70" width="120" height="20" rx="5" fill={INK} /><T x={52} y={84} s={8} c={WARM}>DATA.CSV</T>
        {[0, 1, 2, 3, 4, 5].map((r) => (<line key={r} x1="42" y1={110 + r * 18} x2="162" y2={110 + r * 18} stroke="rgba(18,17,16,0.12)" />))}
        {[0, 1].map((c) => (<line key={c} x1={82 + c * 40} y1="90" x2={82 + c * 40} y2="220" stroke="rgba(18,17,16,0.12)" />))}
        {[0, 1, 2, 3, 4, 5].map((r) => ([0, 1, 2].map((c) => (<rect key={r + '-' + c} x={48 + c * 40} y={97 + r * 18} width={c === 1 ? 22 : 28} height="4" rx="2" fill={r === 0 ? OR3 : INK} opacity={r === 0 ? 0.8 : 0.3} />))))}
      </g>
      <g stroke={OR3} strokeWidth="1.6" fill="none"><path d="M166 145 H196 M190 140 L196 145 L190 150" /></g>
      {/* chat */}
      <g>
        <rect x="202" y="84" width="120" height="30" rx="8" fill={INK} /><T x={212} y={98} s={7.5} c={WARM} o={0.9}>Which branch drove</T><T x={212} y={108} s={7.5} c={WARM} o={0.9}>Q3 overtime cost?</T>
        <rect x="214" y="122" width="108" height="24" rx="8" fill="none" stroke={OR3} strokeWidth="1.2" /><T x={224} y={132} s={7.5} c={OR3}>Site B · +38% —</T><T x={224} y={141} s={7.5} c={OR3}>see chart →</T>
        <circle cx="202" cy="160" r="9" fill={OR} /><T x={202} y={163.5} s={8} c={WARM} a="middle" f={DISP}>AI</T>
        <T x={218} y={164} s={7} c={INK} o={0.55}>CLAUDE API · PANDAS</T>
      </g>
      {/* plotly card */}
      <g><rect x="330" y="64" width="116" height="160" rx="5" fill={INK} /><T x={340} y={80} s={7.5} c={WARM} o={0.7}>INSIGHT #4</T>
        <Spark x={340} y={88} w={96} h={48} c={OR2} />
        <BarRow x={340} y={146} w={96} h={40} n={5} c={AMB} o={0.85} />
        <circle cx="346" cy="200" r="3" fill={OR2} /><T x={353} y={203} s={6.5} c={WARM} o={0.6}>OVERTIME</T>
        <circle cx="400" cy="200" r="3" fill={AMB} /><T x={407} y={203} s={6.5} c={WARM} o={0.6}>BASE</T>
      </g>
      <T x={240} y={262} s={8} c={INK} a="middle" o={0.6}>UPLOAD → ASK IN PLAIN LANGUAGE → INTERACTIVE PLOTLY</T>
      <Chip x={196} y={276} w={54} label="FASTAPI" c={OR3} /><Chip x={256} y={276} w={40} label="REST" c={OR3} />
    </Root>
  );
}
function SceneNextchat() {
  return (
    <Root label="Streamtalk production AI chat">
      <Chrome x={52} y={38} w={376} h={244} fill={INK3} stroke="rgba(255,255,255,0.14)" dot={WARM} />
      {/* sidebar */}
      <rect x="52" y="54" width="88" height="228" fill="rgba(255,255,255,0.04)" />
      <rect x="62" y="64" width="68" height="16" rx="4" fill={OR} /><T x={96} y={75} s={7.5} c={WARM} a="middle">+ NEW</T>
      {[0, 1, 2, 3].map((i) => (<rect key={i} x="62" y={90 + i * 20} width={i === 1 ? 68 : 56} height="7" rx="3.5" fill={WARM} opacity={i === 1 ? 0.7 : 0.25} />))}
      {/* model pills */}
      <Chip x={152} y={62} w={58} label="CLAUDE" c={OR2} /><Chip x={216} y={62} w={40} label="GPT" /><Chip x={262} y={62} w={52} label="GEMINI" />
      <T x={416} y={72} s={7} c={WARM} o={0.45} a="end">EDGE · SSR</T>
      {/* bubbles */}
      <rect x="252" y="92" width="160" height="26" rx="9" fill="rgba(255,255,255,0.1)" /><Lines x={262} y={101} w={140} n={2} c={WARM} o={0.55} gap={8} h={3} />
      <rect x="152" y="128" width="196" height="44" rx="9" fill={OR} opacity="0.94" /><Lines x={164} y={138} w={172} n={3} c={WARM} o={0.75} gap={10} h={3.5} />
      <rect x="252" y="182" width="160" height="26" rx="9" fill="rgba(255,255,255,0.1)" /><Lines x={262} y={191} w={140} n={2} c={WARM} o={0.55} gap={8} h={3} />
      <rect x="152" y="218" width="132" height="24" rx="9" fill={OR} opacity="0.94" /><Lines x={164} y={227} w={90} n={1} c={WARM} o={0.75} h={3.5} />
      <rect x="260" y="225" width="6" height="10" fill={WARM} className="blink" />
      <T x={152} y={256} s={7} c={OR2} o={0.9}>● STREAMING · RESUMABLE (REDIS)</T>
      {/* input */}
      <rect x="152" y="262" width="240" height="14" rx="7" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.15)" />
      <g transform="translate(400 262)"><path d="M0 7 L14 0 L10 12 L6 8 Z" fill={OR2} /></g>
      <T x={240} y={306} s={7.5} c={WARM} a="middle" o={0.45}>NEXT.JS 15 · AI SDK 5 · POSTGRES · NEXTAUTH · LIVE ON VERCEL</T>
    </Root>
  );
}

/* ---------- ACT III ---------- */
function SceneCryptoverse() {
  return (
    <Root label="Coinscope market intelligence">
      {/* ticker strip */}
      {[['BTC', '+2.4'], ['ETH', '+1.1'], ['TON', '-0.8'], ['SOL', '+4.2'], ['XRP', '+0.3']].map(([c, v], i) => (
        <g key={c}><rect x={44 + i * 80} y={34} width="72" height="20" rx="10" fill={INK3} stroke="rgba(255,255,255,0.12)" /><circle cx={57 + i * 80} cy="44" r="5" fill={i % 2 ? AMB : OR2} opacity="0.9" /><T x={67 + i * 80} y={47.5} s={7.5} c={WARM} o={0.85}>{c}</T><T x={109 + i * 80} y={47.5} s={7} c={v.startsWith('+') ? OR2 : WARM} o={v.startsWith('+') ? 1 : 0.5} a="end">{v}%</T></g>
      ))}
      {/* main chart */}
      <rect x="44" y="66" width="284" height="180" rx="5" fill={INK3} stroke="rgba(255,255,255,0.1)" />
      {[0.25, 0.5, 0.75].map((t, i) => (<line key={i} x1="50" y1={66 + 180 * t} x2="322" y2={66 + 180 * t} stroke="rgba(255,255,255,0.06)" />))}
      <path d="M56 210 C 96 186, 116 208, 150 172 C 184 138, 208 168, 240 128 C 264 100, 292 112, 316 84" fill="none" stroke={OR2} strokeWidth="2" />
      <path d="M56 210 C 96 186, 116 208, 150 172 C 184 138, 208 168, 240 128 C 264 100, 292 112, 316 84 L316 240 L56 240 Z" fill={OR2} opacity="0.1" />
      <circle cx="316" cy="84" r="3.5" fill={OR2} /><circle cx="316" cy="84" r="8" fill="none" stroke={OR2} strokeWidth="1" opacity="0.4" className="blink" />
      <T x={54} y={82} s={8} c={WARM} o={0.6}>BTC / USDT — 24H</T><T x={318} y={78} s={8} c={OR2} a="end">68,4K</T>
      {/* depth panel */}
      <rect x="338" y="66" width="98" height="180" rx="5" fill={INK3} stroke="rgba(255,255,255,0.1)" />
      <T x={346} y={80} s={7} c={WARM} o={0.5}>DEPTH</T>
      {[0.9, 0.65, 0.8, 0.45, 0.7, 0.35, 0.55, 0.25].map((v, i) => (<rect key={i} x={i % 2 ? 387 : 387 - 41 * v} y={90 + i * 18} width={41 * v} height="9" rx="2" fill={i % 2 ? OR2 : AMB} opacity={i % 2 ? 0.85 : 0.55} />))}
      <line x1="387" y1="88" x2="387" y2="236" stroke="rgba(255,255,255,0.2)" />
      {/* telegram feed */}
      <rect x="44" y="258" width="392" height="42" rx="6" fill={INK3} stroke="rgba(255,255,255,0.1)" />
      <Plane x={56} y={268} s={0.75} />
      <T x={90} y={274} s={7.5} c={OR2}>SIGNAL FEED</T>
      <Lines x={90} y={280} w={220} n={2} c={WARM} o={0.35} gap={8} h={3} />
      <Chip x={352} y={272} w={70} label="LIVE · tRPC" c={OR2} />
    </Root>
  );
}
function SceneVisactor() {
  return (
    <Root label="Chartfolio visualization starter">
      {/* bento */}
      <rect x="44" y="42" width="188" height="128" rx="6" fill={PAPER} stroke="rgba(18,17,16,0.18)" />
      <Donut cx={102} cy={106} r={34} c1={OR3} c2={AMB} track="rgba(18,17,16,0.1)" />
      <T x={102} y={110} s={11} c={INK} f={DISP} a="middle">64%</T>
      {[['SERIES A', OR3], ['SERIES B', AMB], ['OTHER', INK]].map(([lb, c], i) => (<g key={lb}><circle cx="158" cy={84 + i * 18} r="3.5" fill={c} opacity={c === INK ? 0.3 : 1} /><T x={167} y={87 + i * 18} s={7} c={INK} o={0.6}>{lb}</T></g>))}
      <rect x="240" y="42" width="196" height="128" rx="6" fill={INK} />
      <T x={250} y={58} s={7.5} c={WARM} o={0.6}>STACKED · QUARTERLY</T>
      {[0, 1, 2, 3, 4, 5].map((i) => { const a = [0.5, 0.72, 0.6, 0.9, 0.68, 0.82][i]; return (<g key={i}><rect x={252 + i * 30} y={158 - 90 * a} width="19" height={90 * a * 0.55} fill={OR2} /><rect x={252 + i * 30} y={158 - 90 * a * 0.45} width="19" height={90 * a * 0.45} fill={AMB} opacity="0.85" /></g>); })}
      <rect x="44" y="178" width="252" height="118" rx="6" fill={INK} />
      <T x={54} y={194} s={7.5} c={WARM} o={0.6}>STREAM · REALTIME</T>
      <path d="M54 268 C 94 240, 120 262, 160 236 C 200 212, 236 238, 286 208" fill="none" stroke={OR2} strokeWidth="2" />
      <path d="M54 276 C 100 258, 130 274, 176 254 C 218 236, 250 254, 286 232" fill="none" stroke={AMB} strokeWidth="1.6" opacity="0.8" />
      <path d="M54 268 C 94 240, 120 262, 160 236 C 200 212, 236 238, 286 208 L286 288 L54 288 Z" fill={OR2} opacity="0.1" />
      <rect x="304" y="178" width="132" height="118" rx="6" fill={PAPER} stroke="rgba(18,17,16,0.18)" />
      <T x={314} y={194} s={7.5} c={INK} o={0.6}>SCATTER</T>
      {[[330, 262, 4], [348, 236, 6], [366, 252, 3], [382, 220, 7], [398, 240, 4.5], [412, 210, 5.5], [352, 274, 3.5], [396, 268, 3]].map(([x, y, r], i) => (<circle key={i} cx={x} cy={y} r={r} fill={i % 3 === 0 ? OR3 : i % 3 === 1 ? AMB : INK} opacity={i % 3 === 2 ? 0.35 : 0.8} />))}
      <Chip x={392} y={50} w={36} label="MIT" c={OR2} />
    </Root>
  );
}
function SceneOilrig() {
  return (
    <Root label="Rigguard field HSE operations">
      {/* horizon + sun */}
      <line x1="10" y1="238" x2="470" y2="238" stroke={INK} strokeWidth="1.2" opacity="0.5" />
      <circle cx="386" cy="84" r="30" fill={OR} opacity="0.25" /><circle cx="386" cy="84" r="20" fill={OR} opacity="0.35" />
      {/* derrick */}
      <g stroke={INK} strokeWidth="2" fill="none">
        <path d="M118 238 L150 74 L182 238" />
        <path d="M126 200 H174 M132 168 H168 M138 136 H162 M144 104 H156" strokeWidth="1.4" />
        <path d="M126 200 L168 168 M174 200 L132 168 M132 168 L162 136 M168 168 L138 136 M138 136 L156 104 M162 136 L144 104" strokeWidth="1" opacity="0.7" />
        <rect x="146" y="64" width="8" height="10" fill={OR} stroke="none" />
      </g>
      {/* pump silhouette */}
      <g stroke={INK} strokeWidth="1.8" fill="none" opacity="0.8"><path d="M236 238 V210 L262 196 M262 196 L284 210 M262 196 V188" /><circle cx="284" cy="216" r="9" /><path d="M228 238 h64" /></g>
      {/* shield */}
      <g><path d="M362 128 L396 140 V166 C396 186 380 196 362 202 C344 196 328 186 328 166 V140 Z" fill={PAPER} stroke={OR3} strokeWidth="2" /><path d="m348 166 10 10 20-22" fill="none" stroke={OR3} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /><T x={362} y={222} s={7.5} c={INK} a="middle" o={0.7}>HSE PROFILE</T></g>
      {/* stop cards */}
      {[[46, 252, OR, 'STOP CARD'], [140, 258, AMB, 'DAILY RPT'], [234, 252, INK, 'PERMIT']].map(([x, y, c, lb], i) => (
        <g key={lb} transform={`rotate(${i === 1 ? -3 : i === 2 ? 2 : 0} ${x} ${y})`}>
          <rect x={x} y={y} width="78" height="44" rx="4" fill={PAPER} stroke={c} strokeWidth="1.6" opacity={c === INK ? 0.75 : 1} />
          <rect x={x} y={y} width="78" height="12" rx="4" fill={c} opacity={c === INK ? 0.75 : 1} />
          <T x={x + 39} y={y + 9} s={6.5} c={WARM} a="middle">{lb}</T>
          <Lines x={x + 8} y={y + 20} w={60} n={2} c={INK} o={0.35} gap={8} h={3} />
        </g>
      ))}
      {/* hard hat */}
      <g transform="translate(366 256)"><path d="M0 20 a26 26 0 0 1 52 0" fill={OR} /><rect x="-6" y="20" width="64" height="7" rx="3.5" fill={INK} /><rect x="20" y="-4" width="12" height="12" rx="3" fill={OR3} /></g>
      <T x={64} y={54} s={8} c={INK} o={0.6}>EXT. OIL FIELD — DAY</T>
      <Chip x={44} y={64} w={92} label="FIELD-READY UI" c={OR3} />
    </Root>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- component map, not a constant
export const SCENES = {
  ogmob: SceneOgmob,
  ofogh2: SceneOfogh2,
  mns: SceneMns,
  split: SceneSplit,
  frost: SceneFrost,
  bots: SceneBots,
  report: SceneReport,
  nextchat: SceneNextchat,
  cryptoverse: SceneCryptoverse,
  visactor: SceneVisactor,
  oilrig: SceneOilrig,
};
